import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import './ProgramsList.css';

const getWeekCount = (program) => {
  if (!program.weeks || typeof program.weeks !== 'object') return 0;
  return Object.keys(program.weeks).length;
};

const getCardDescription = (program) => (
  program.short_description || program.description || ''
);

const getProgramLevelLabel = (program) => {
  if (program.level_name) {
    return /^\d+$/.test(String(program.level_name))
      ? `Nivå ${program.level_name}`
      : program.level_name;
  }
  if (program.level != null && program.level !== '') {
    return `Nivå ${program.level}`;
  }
  return null;
};

export default function ProgramsList() {
  const [programs, setPrograms] = useState([]);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchPrograms();
  }, []);

  const fetchPrograms = async () => {
    try {
      setLoading(true);
      const response = await axios.get(
        `${process.env.REACT_APP_BACKEND_URL}/api/programs`,
        { withCredentials: true }
      );
      setPrograms(response.data.result || []);
      setError('');
    } catch (err) {
      console.error('Error fetching programs:', err);
      setError('Kunne ikke laste programmer');
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return <div className="prog-list-loading">Laster programmer…</div>;
  }

  return (
    <div className="prog-list">
      <div className="prog-list-container">
        <div className="prog-list-top">
          <Link to="/dashboard" className="prog-list-back">
            <i className="fas fa-arrow-left" aria-hidden="true" />
            Tilbake til hovedsiden
          </Link>
        </div>

        <header className="prog-list-header">
          <h1>Alle programmer</h1>
          <p>Utforsk treningsprogrammene våre – bygget for glede, progresjon og mestring.</p>
        </header>

        {error && <div className="prog-list-error">{error}</div>}

        <div className="prog-list-grid">
          {programs.length > 0 ? (
            programs.map((program) => {
              const weekCount = getWeekCount(program);
              const description = getCardDescription(program);
              const imageUrl = program.image_url || program.src || '/img/running.jpeg';
              const isFree = program.isFree === 1 || program.isFree === true;
              const levelLabel = getProgramLevelLabel(program);

              return (
                <Link
                  key={program.id}
                  to={`/programs/${program.id}`}
                  className="prog-list-card"
                >
                  <div className="prog-list-card-media">
                    <img src={imageUrl} alt="" />
                    {weekCount > 0 && (
                      <span className="prog-list-card-badge">{weekCount} uker</span>
                    )}
                    {levelLabel && (
                      <span className="prog-list-card-badge prog-list-card-badge--level">
                        {levelLabel}
                      </span>
                    )}
                  </div>
                  <div className="prog-list-card-body">
                    <h2>{program.name}</h2>
                    {description && (
                      <p className="prog-list-card-desc">{description}</p>
                    )}
                    <div className="prog-list-card-meta">
                      <span className="prog-list-pill">
                        <i className={`fas ${isFree ? 'fa-gift' : 'fa-star'}`} aria-hidden="true" />
                        {isFree ? 'Gratis' : 'Premium'}
                      </span>
                      {weekCount > 0 && (
                        <span className="prog-list-pill">
                          <i className="far fa-calendar-alt" aria-hidden="true" />
                          {weekCount} uker
                        </span>
                      )}
                      {program.businessName && (
                        <span className="prog-list-pill">
                          <i className="fas fa-building" aria-hidden="true" />
                          {program.businessName}
                        </span>
                      )}
                    </div>
                    <span className="prog-list-card-cta">
                      Se programplan
                      <i className="fas fa-arrow-right" aria-hidden="true" />
                    </span>
                  </div>
                </Link>
              );
            })
          ) : (
            <p className="prog-list-empty">Ingen programmer funnet</p>
          )}
        </div>
      </div>
    </div>
  );
}
